import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/use-auth";
import LoginForm from "../components/login-form";
import { Loader2, Download, Share, SquarePlus } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogOverlay,
} from "../components/ui/dialog";

export default function LoginPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [isIosSheetOpen, setIsIosSheetOpen] = useState(false);

  const env = import.meta.env.VITE_ENV;
  const isMobile = env === "mobile";

  useEffect(() => {
    if (!loading && user) {
      navigate("/feed", { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading || user) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <img
            src="/img/logo.png"
            alt="NemaNeide Logo"
            width={250}
            height={70}
            className="object-contain"
          />
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <main className="relative flex min-h-screen w-full items-center justify-center bg-background p-4 font-body pt-[calc(1rem_+_env(safe-area-inset-top))] pb-[calc(1rem_+_env(safe-area-inset-bottom))]">
      <img
        src="/ursalogin.png"
        alt="Background"
        className="absolute inset-0 w-full h-full object-cover object-top z-0"
      />
      <div className="absolute inset-0 bg-black/50 z-10" />
      <div className="relative z-20 w-full max-w-md space-y-4 drop-shadow-45">
        <div className="text-center">
          <img
            src="/img/logo.png"
            alt="NemaNeide Logo"
            width={250}
            height={70}
            className="mx-auto object-contain"
          />
          <h1 className="mt-6 text-4xl font-bold tracking-tight text-foreground font-headline">
            Dobrodošli u NemaNeide
          </h1>
          <p className="mt-2 text-lg text-muted-foreground">
            Vaša osobna fitness zajednica vas čeka.
          </p>
        </div>
        <LoginForm />
        <p className="text-center text-sm text-muted-foreground pt-4">
          Kontaktirajte trenera da biste dobili pristup.
        </p>
      </div>

      {/* Only show download button and dialogs if not in mobile environment */}
      {!isMobile && (
        <>
          <Dialog>
            <DialogTrigger asChild>
              <Button className="fixed bottom-0 left-0 right-0 w-full h-14 md:hidden rounded-none z-30 gap-2 bg-accent hover:bg-accent/90 shadow-lg border-t-2 border-border/20">
                <Download className="h-5 w-5" /> Preuzmi aplikaciju
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Preuzmi aplikaciju</DialogTitle>
                <DialogDescription>
                  Odaberi platformu koju koristiš.
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <Button
                    className="w-full bg-accent hover:bg-[#e73e7b]"
                    onClick={() => setIsIosSheetOpen(true)}
                  >
                    iOS
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">
                    Za iPhone
                  </p>
                </div>
                <div>
                  <a href="/NemaNeide.apk" download>
                    <Button className="w-full bg-accent hover:bg-[#e73e7b]">
                      Android
                    </Button>
                  </a>
                  <p className="text-xs text-muted-foreground mt-2">
                    Za Samsung, Xaomi, Huawey, Onenote...
                  </p>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={isIosSheetOpen} onOpenChange={setIsIosSheetOpen}>
            <DialogOverlay className="bg-black/50" />
            <DialogContent className="w-full max-w-sm m-0 p-4 border-none bg-transparent shadow-none flex flex-col justify-end items-center text-center focus-visible:outline-none">
              <div className="bg-card/95 p-6 rounded-xl text-card-foreground">
                <p className="text-lg font-medium">
                  Potrebna su samo dva dodira.
                </p>
                <p className="text-muted-foreground mt-2">
                  Dodirni{" "}
                  <span className="inline-block align-middle bg-white rounded-md p-0.5 mx-1">
                    <Share className="h-5 w-5 text-[#2664c7]" />
                  </span>{" "}
                  ikonu na dnu zaslona, zatim se spusti i pronađi{" "}
                  <span className="font-semibold inline-flex items-center gap-1 align-middle bg-white text-[#2664c7] rounded-md px-1.5 py-0.5 mx-0.5">
                    Dodaj na početni zaslon <SquarePlus className="h-4 w-4" />
                  </span>
                  .
                </p>
                <p className="text-muted-foreground mt-2">
                  Nakon toga klikni{" "}
                  <span className="font-semibold inline-block align-middle bg-white text-[#2664c7] rounded-md px-1.5 py-0.5 mx-0.5">
                    Dodaj
                  </span>{" "}
                  (gornji desni kut).
                </p>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </main>
  );
}
